import { ZodSchema, z as zod } from 'zod/v3';
import { Ty } from '../ty';
import _ from 'lodash';
import { tryUnion } from './try-union';
import { Assert } from '@cotera/utilities';
import { ExtendedAttributeType, shorthandToTy } from '../ty/ty';
import { prettyPrint } from './pretty-print';
import { PRIMITIVE_TYPE_INFERENCE_RULES } from '../parser/primitive-type-inference-rules';

export type Infer<T> = T extends Def<infer Inner> ? Inner : never;

type ListElt<T> = T extends (infer E)[] ? E : never;
type RecordVal<T> = T extends Record<string, infer E> ? E : never;

type SchemaShape<T> =
  | {
      /** May assert that: Schema.ty.ty.k is: 'primitive' | 'range' | 'enum' */
      readonly t: 'base';
    }
  | {
      readonly t: 'array';
      readonly elementSchema: Def<ListElt<T>>;
    }
  | {
      readonly t: 'record';
      readonly recordValueSchema: Def<RecordVal<T>>;
    }
  | {
      readonly t: 'struct';
      readonly fieldsSchema: { [Key in keyof T]: Def<T[Key]> };
    }
  | {
      readonly t: 'discriminatedUnion';
      readonly discriminator: string;
      readonly options: readonly Def<unknown>[];
    }
  | {
      readonly t: 'lazy';
      readonly get: () => Def<T>;
    }
  | {
      readonly t: 'recordWithKey';
      readonly keySchema: zod.ZodType<string>;
      readonly valueSchema: Def<unknown>;
      readonly recordNullable: boolean;
    };

type DefaultValue<T> =
  | {
      readonly specified: true;
      readonly val: T;
    }
  | {
      readonly specified: false;
    };

/**
 * A schema for the intended kind of Era data (e.g. LinkedIn API profile get responses).
 *
 * `T` - The type of the the JavaScript representation of the Era data.
 *
 */
export type Def<T> = {
  /**
   * The type of the era data.
   */
  ty: Ty.ExtendedAttributeType;

  /**
   * The possibly recursive structure of the schema.
   */
  shape: SchemaShape<T>;

  /**
   * A helpful description of the era data.
   */
  description: string | null;

  /**
   * The default value for undefined data.
   */
  defaultValue: DefaultValue<T>;

  /**
   * Whether the schema allows for null values.
   */
  isNullable: boolean;

  /**
   * A ZodSchema that validates javascript data as era data conforming to
   * this Erod schema specification.
   *
   * @param opts.strict - When true, the schema will be strict about null/undefined values.
   *                      When false (default), the schema will allow null/undefined values
   *                      and convert them to null or a supplied default value.
   */
  zodSchema(opts?: { strict?: boolean }): ZodSchema<T>;

  /**
   * A pretty string representation.
   */
  prettyPrint(): string;

  /**
   * If the schema has the shape of an object,
   * returns the schema of the desired field member.
   * For struct schemas: returns the schema of the field at the given key.
   * For record schemas: returns the schema of values (Def<RecordVal<T>>).
   */
  get(key: string): Def<unknown> | null;

  /**
   * Returns the same schema, but with a description specified.
   *
   * compare to zod.z.describe()
   */
  describe(description: string | null): Def<T>;

  /**
   * Returns the same schema, but allows for data to be null.
   *
   * compare to zod.z.nullable()
   */
  nullable(): Def<T | null>;

  /**
   * Returns the same schema, but specifies a valid default value that
   * undefined data should represent.
   */
  default(val: T): Def<T>;

  /**
   * Returns a schema for an array, each of whose elements must conform to this schema.
   */
  array(): Def<T[]>;

  /**
   * Returns a schema for a record, each of whose values must conform to this schema.
   */
  record(): Def<Record<string, T>>;

  /**
   * Extends this schema with additional fields (only works for struct schemas).
   * Similar to zod's .extend() method.
   *
   * @param additionalFields - Object containing new fields to add or existing fields to override
   * @returns New schema with combined fields
   */
  extend<AdditionalDefs extends Record<string, Def<any>>>(
    additionalFields: AdditionalDefs
  ): Def<T & { [K in keyof AdditionalDefs]: Infer<AdditionalDefs[K]> }>;
};

/**
 * The (only) factory that produces Def<T> schemas.
 *
 * Required: Any Def<K> used as input to a factory function
 * must have been returned from a factory function from this Factory.
 */
export interface SchemaFactory {
  super: () => Def<Ty.Scalar>;
  any: () => Def<Ty.Scalar>;
  boolean: () => Def<boolean>;
  date: () => Def<Date>;
  timestamp: () => Def<Date>;
  int: () => Def<number>;
  float: () => Def<number>;
  number: () => Def<number>;
  literal: <K extends string>(lit: K) => Def<K>;
  enum: <K extends string>(variants: K[]) => Def<K>;
  string: () => Def<string>;
  array: <K>(eltSchema: Def<K>) => Def<K[]>;
  record: <K>(inner: Def<K>) => Def<Record<string, K>>;
  /** Record with custom key schema (e.g. for identifier validation) */
  recordWithKey: <V>(
    keySchema: zod.ZodType<string>,
    valueSchema: Def<V>
  ) => Def<Record<string, V>>;
  object: <
    Defs extends { [Key in keyof Tys]: Def<Tys[Key]> },
    Tys extends { [Key in keyof Defs]: Infer<Defs[Key]> }
  >(
    defs: Defs
  ) => Def<Tys>;
  struct: <
    Defs extends { [Key in keyof Tys]: Def<Tys[Key]> },
    Tys extends { [Key in keyof Defs]: Infer<Defs[Key]> }
  >(
    defs: Defs
  ) => Def<Tys>;

  /**  */
  union: <Ts extends readonly unknown[]>(
    options: DistributeDef<Ts>
  ) => Def<Ts[number]>;

  /**
   * Creates a schema that validates a discriminated union.
   * Each option must be a struct schema with the discriminator key as a literal.
   * Uses Zod's discriminatedUnion for efficient validation and better error messages.
   */
  discriminatedUnion: <D extends string, Ts extends readonly object[]>(
    discriminator: D,
    options: DiscriminatedUnionOptions<D, Ts>
  ) => Def<Ts[number]>;

  /**
   * Creates a lazy schema for recursive definitions.
   * The thunk is evaluated on first access.
   */
  lazy: <T>(fn: () => Def<T>) => Def<T>;
}

/**
 * A schema that validates discriminated unions using Zod's discriminatedUnion.
 * Wraps the merged struct schema for Era type compatibility.
 */
class DiscriminatedUnionSchema<T> implements Def<T> {
  readonly shape: {
    readonly t: 'discriminatedUnion';
    readonly discriminator: string;
    readonly options: readonly Def<unknown>[];
  };

  constructor(
    private readonly _inner: Def<T>,
    discriminator: string,
    options: readonly Def<unknown>[]
  ) {
    this.shape = {
      t: 'discriminatedUnion',
      discriminator,
      options,
    };
    Object.freeze(this);
  }

  get ty(): Ty.ExtendedAttributeType {
    return this._inner.ty;
  }

  get description(): string | null {
    return this._inner.description;
  }

  get isNullable(): boolean {
    return this._inner.isNullable;
  }

  get defaultValue(): DefaultValue<T> {
    return this._inner.defaultValue;
  }

  zodSchema(opts?: { strict?: boolean }): ZodSchema<T> {
    const zodSchemas = this.shape.options.map((opt) =>
      opt.zodSchema(opts ?? {})
    );
    // Struct schemas produce zod.object(); Zod's discriminatedUnion expects ZodObject.
    // We validate at construction that options are structs, so this is safe.
    type ZodDiscriminatedUnionOptions = Parameters<
      typeof zod.discriminatedUnion
    >[1];
    const withoutNullable = zod.discriminatedUnion(
      this.shape.discriminator,
      zodSchemas as unknown as ZodDiscriminatedUnionOptions
    ) as unknown as ZodSchema<T>;

    let result: ZodSchema<T> = withoutNullable;
    if (this.isNullable) {
      result = (
        opts?.strict
          ? withoutNullable.nullable()
          : withoutNullable.nullish().default(null)
      ) as ZodSchema<T>;
    }
    if (this.defaultValue.specified) {
      result = result.default(
        this.defaultValue.val as Exclude<T, undefined>
      ) as ZodSchema<T>;
    }
    if (this.description) {
      result = result.describe(this.description) as ZodSchema<T>;
    }
    return result;
  }

  prettyPrint(): string {
    return prettyPrint(this);
  }

  get(key: string): Def<unknown> | null {
    return this._inner.get(key);
  }

  describe(description: string | null): Def<T> {
    return new DiscriminatedUnionSchema(
      this._inner.describe(description),
      this.shape.discriminator,
      this.shape.options
    );
  }

  nullable(): Def<T | null> {
    return new DiscriminatedUnionSchema(
      this._inner.nullable(),
      this.shape.discriminator,
      this.shape.options
    );
  }

  default(val: T): Def<T> {
    return new DiscriminatedUnionSchema(
      this._inner.default(val),
      this.shape.discriminator,
      this.shape.options
    );
  }

  array(): Def<T[]> {
    return this._inner.array();
  }

  record(): Def<Record<string, T>> {
    return this._inner.record();
  }

  extend<AdditionalDefs extends Record<string, Def<any>>>(
    _additionalFields: AdditionalDefs
  ): Def<T & { [K in keyof AdditionalDefs]: Infer<AdditionalDefs[K]> }> {
    throw new Error('extend() cannot be called on discriminatedUnion schemas');
  }
}

/**
 * A schema that lazily resolves its inner schema for recursive definitions.
 */
class LazySchema<T> implements Def<T> {
  readonly shape: { readonly t: 'lazy'; readonly get: () => Def<T> };
  private _resolved: Def<T> | null = null;

  constructor(get: () => Def<T>) {
    this.shape = { t: 'lazy', get };
    // Do not freeze: _inner getter mutates _resolved on first access; freezing
    // would make that assignment throw in strict mode (ES modules).
  }

  private get _inner(): Def<T> {
    if (this._resolved === null) {
      this._resolved = this.shape.get();
    }
    return this._resolved;
  }

  get ty(): Ty.ExtendedAttributeType {
    return this._inner.ty;
  }

  get description(): string | null {
    return this._inner.description;
  }

  get isNullable(): boolean {
    return this._inner.isNullable;
  }

  get defaultValue(): DefaultValue<T> {
    return this._inner.defaultValue;
  }

  zodSchema(opts?: { strict?: boolean }): ZodSchema<T> {
    return zod.lazy(() => this._inner.zodSchema(opts ?? {}));
  }

  prettyPrint(): string {
    return prettyPrint(this);
  }

  get(key: string): Def<unknown> | null {
    return this._inner.get(key);
  }

  describe(description: string | null): Def<T> {
    return new LazySchema(() => this._inner.describe(description));
  }

  nullable(): Def<T | null> {
    return new LazySchema(() => this._inner.nullable());
  }

  default(val: T): Def<T> {
    return new LazySchema(() => this._inner.default(val));
  }

  array(): Def<T[]> {
    return this._inner.array();
  }

  record(): Def<Record<string, T>> {
    return this._inner.record();
  }

  extend<AdditionalDefs extends Record<string, Def<any>>>(
    additionalFields: AdditionalDefs
  ): Def<T & { [K in keyof AdditionalDefs]: Infer<AdditionalDefs[K]> }> {
    return this._inner.extend(additionalFields);
  }
}

/**
 * A schema for records with custom key validation.
 */
class RecordWithKeySchema<V> implements Def<Record<string, V>> {
  readonly shape: {
    readonly t: 'recordWithKey';
    readonly keySchema: zod.ZodType<string>;
    readonly valueSchema: Def<V>;
    readonly recordNullable: boolean;
  };

  constructor(
    keySchema: zod.ZodType<string>,
    valueSchema: Def<V>,
    recordNullable = false
  ) {
    this.shape = { t: 'recordWithKey', keySchema, valueSchema, recordNullable };
    Object.freeze(this);
  }

  get ty(): Ty.ExtendedAttributeType {
    // When valueSchema is RecordWithKeySchema, use recordTyNn to avoid infinite
    // recursion (valueSchema.record() would return a schema that calls back here).
    const baseTy =
      this.shape.valueSchema.shape?.t === 'recordWithKey'
        ? (Ty.CreateImmutable.recordTyNn(
            this.shape.valueSchema.ty as Ty.ImmutableTy
          ) as Ty.ExtendedAttributeType)
        : this.shape.valueSchema.record().ty;
    return this.shape.recordNullable ? { ...baseTy, nullable: true } : baseTy;
  }

  get description(): string | null {
    return this.shape.valueSchema.description;
  }

  get isNullable(): boolean {
    return this.shape.recordNullable;
  }

  get defaultValue(): DefaultValue<Record<string, V>> {
    return { specified: false };
  }

  zodSchema(opts?: { strict?: boolean }): ZodSchema<Record<string, V>> {
    const recordSchema = zod.record(
      this.shape.keySchema,
      this.shape.valueSchema.zodSchema(opts ?? {})
    );
    return (
      this.shape.recordNullable ? recordSchema.nullable() : recordSchema
    ) as ZodSchema<Record<string, V>>;
  }

  prettyPrint(): string {
    return prettyPrint(this);
  }

  get(_key: string): Def<V> | null {
    return this.shape.valueSchema;
  }

  describe(description: string | null): Def<Record<string, V>> {
    return new RecordWithKeySchema(
      this.shape.keySchema,
      this.shape.valueSchema.describe(description),
      this.shape.recordNullable
    );
  }

  nullable(): Def<Record<string, V> | null> {
    if (this.shape.recordNullable) {
      return this;
    }
    return new RecordWithKeySchema(
      this.shape.keySchema,
      this.shape.valueSchema,
      true
    );
  }

  default(_val: Record<string, V>): Def<Record<string, V>> {
    throw new Error('default() is not supported on recordWithKey schemas');
  }

  array(): Def<Record<string, V>[]> {
    return this.shape.valueSchema.record().array();
  }

  record(): Def<Record<string, Record<string, V>>> {
    return new RecordWithKeySchema(
      this.shape.keySchema,
      this as Def<Record<string, V>>
    );
  }

  extend<AdditionalDefs extends Record<string, Def<any>>>(
    _additionalFields: AdditionalDefs
  ): Def<
    Record<string, V> & {
      [K in keyof AdditionalDefs]: Infer<AdditionalDefs[K]>;
    }
  > {
    throw new Error('extend() cannot be called on recordWithKey schemas');
  }
}

/**
 * An implementation of the SchemaFactory.
 *
 * (Required: Any Def<T> used as input to a factory function must have
 * been returned from a factory function from this Factory)
 */
export class Schema<T> implements Def<T> {
  get ty(): Ty.ExtendedAttributeType {
    return this._immutableTy;
  }

  get shape(): SchemaShape<T> {
    return this._shape;
  }

  get description(): string | null {
    return this._description;
  }

  get isNullable(): boolean {
    return this.ty.nullable;
  }

  get defaultValue(): DefaultValue<T> {
    return this._defaultValue;
  }

  zodSchema = (opts?: { strict?: boolean }): ZodSchema<T> => {
    return Schema.createZodSchema(
      this._immutableTy,
      this._description,
      this._defaultValue,
      opts ?? {}
    );
  };

  prettyPrint = (): string => {
    return prettyPrint(this);
  };

  // TODO: update Def<T> type
  get = (key: string): Def<T> | null => {
    if (this._immutableTy.ty.k !== 'struct') {
      return null;
    }

    const immutableFieldTy = this._immutableTy.ty.fields[key];

    if (immutableFieldTy === undefined) {
      return null;
    }

    return Schema._GlobalSchemaStore.get(immutableFieldTy) ?? null;
  };

  describe = (description: string): Def<T> => {
    const persistedOpts = {
      defaultValue: this._constructorOpts.defaultValue,
    };

    return new Schema(Ty.CreateImmutable.copy(this._immutableTy), {
      ...persistedOpts,
      description,
    });
  };

  nullable = (): Def<T | null> =>
    new Schema(Ty.CreateImmutable.nullable(this._immutableTy));

  default = (val: T): Def<T> => {
    const persistedOpts = {}; // as a reminder to persist desired opts if more are added in the future

    return new Schema(Ty.CreateImmutable.copy(this._immutableTy), {
      ...persistedOpts,
      defaultValue: { specified: true, val },
    });
  };

  array = (): Def<T[]> =>
    new Schema(Ty.CreateImmutable.arrayTyNn(this._immutableTy));

  record = (): Def<Record<string, T>> =>
    new Schema(Ty.CreateImmutable.recordTyNn(this._immutableTy));

  extend = <AdditionalDefs extends Record<string, Def<any>>>(
    additionalFields: AdditionalDefs
  ): Def<T & { [K in keyof AdditionalDefs]: Infer<AdditionalDefs[K]> }> => {
    // Only works for struct schemas
    if (this._immutableTy.ty.k !== 'struct') {
      throw new Error('extend() can only be called on struct schemas');
    }

    // Convert additional fields to ImmutableTy format
    const additionalFieldsTy: Record<string, Ty.ImmutableTy> = _.mapValues(
      additionalFields,
      <K>(def: Def<K>) => (def as Schema<K>)._immutableTy
    ) as unknown as Record<string, Ty.ImmutableTy>;

    // Merge the existing fields with additional fields
    const mergedFields = {
      ...this._immutableTy.ty.fields,
      ...additionalFieldsTy,
    };

    // Create new struct type with merged fields
    const mergedStructTy = Ty.CreateImmutable.structTyNn(mergedFields);

    return new Schema<
      T & { [K in keyof AdditionalDefs]: Infer<AdditionalDefs[K]> }
    >(mergedStructTy);
  };

  static super = (): Def<Ty.Scalar> =>
    new Schema<Ty.Scalar>(Ty.CreateImmutable.primitiveTyNn('super'));

  static any = (): Def<Ty.Scalar> => this.super();

  static boolean = (): Def<boolean> =>
    new Schema<boolean>(Ty.CreateImmutable.primitiveTyNn('boolean'));

  static date = (): Def<Date> =>
    new Schema<Date>(Ty.CreateImmutable.primitiveTyNn('timestamp'));

  static timestamp = (): Def<Date> =>
    new Schema<Date>(Ty.CreateImmutable.primitiveTyNn('timestamp'));

  static int = (): Def<number> =>
    new Schema<number>(Ty.CreateImmutable.primitiveTyNn('int'));

  static float = (): Def<number> =>
    new Schema<number>(Ty.CreateImmutable.primitiveTyNn('float'));

  static number = (): Def<number> => this.float();

  static literal = <K extends string>(lit: K): Def<K> => this.enum([lit]);

  static enum = <K extends string>(variants: readonly K[]): Def<K> =>
    new Schema<K>(Ty.CreateImmutable.enumTyNn(variants));

  static string = (): Def<string> =>
    new Schema(Ty.CreateImmutable.primitiveTyNn('string'));

  /**
   * Returns the ImmutableTy for a Def. Schema has _immutableTy directly.
   * LazySchema and other Defs without _immutableTy get a placeholder ty
   * and are registered in _GlobalSchemaStore so array/record lookup succeeds.
   */
  private static getImmutableTyForDef<K>(def: Def<K>): Ty.ImmutableTy {
    const schemaDef = def as Schema<K>;
    if (schemaDef._immutableTy !== undefined) {
      return schemaDef._immutableTy;
    }
    const placeholderTy = Ty.CreateImmutable.structTyNn({
      [`__erodDef_${Math.random().toString(36)}`]:
        Ty.CreateImmutable.primitiveTyNn('string'),
    });
    Schema._GlobalSchemaStore.set(placeholderTy, def);
    return placeholderTy;
  }

  static array = <K>(eltSchema: Def<K>): Def<K[]> =>
    new Schema<K[]>(
      Ty.CreateImmutable.arrayTyNn(Schema.getImmutableTyForDef(eltSchema))
    );

  static record = <K>(inner: Def<K>): Def<Record<string, K>> =>
    new Schema<Record<string, K>>(
      Ty.CreateImmutable.recordTyNn(Schema.getImmutableTyForDef(inner))
    );

  static object = <
    Defs extends { [Key in keyof Tys]: Def<Tys[Key]> },
    Tys extends { [Key in keyof Defs]: Infer<Defs[Key]> }
  >(
    defs: Defs
  ): Def<Tys> => {
    const immutableFieldsTy: Record<string, Ty.ImmutableTy> = {};
    for (const key of Object.keys(defs) as (keyof Defs)[]) {
      const def = defs[key] as Def<unknown>;
      const schemaDef = def as Schema<unknown>;
      if (schemaDef._immutableTy !== undefined) {
        immutableFieldsTy[key as string] = schemaDef._immutableTy;
      } else {
        // Def is not a Schema (e.g. ExtendedAttributeTypeErodSchema).
        // Create a unique placeholder and register the def so struct lookup succeeds.
        const placeholderTy = Ty.CreateImmutable.structTyNn({
          [`__erodCustom_${Math.random().toString(36)}`]:
            Ty.CreateImmutable.primitiveTyNn('string'),
        });
        Schema._GlobalSchemaStore.set(placeholderTy, def);
        immutableFieldsTy[key as string] = placeholderTy;
      }
    }

    const immutableStructTy = Ty.CreateImmutable.structTyNn(immutableFieldsTy);

    return new Schema<Tys>(immutableStructTy);
  };

  static struct = this.object;

  /**
   * Creates a schema that validates a union.
   *
   * The era type system is not yet powerful enough to express a union.
   * In the interim, the constructed era type will be looser than
   * desired (eg unioned structs will just combine all the fields and
   * make non-common ones nullable). See specs for {@link tryUnion}.
   *
   * However, the zodschema will still encode the desired union-constraints.
   */
  static union = <Ts extends readonly unknown[]>(
    options: DistributeDef<Ts>
  ): Def<Ts[number]> => {
    if (options.length === 0) {
      throw new Error('must union with at least one type');
    }

    if (options.length === 1) {
      return Assert.assertExists(options[0]);
    }

    const tyRes = tryUnion(options.map((opt) => opt.ty));

    if (tyRes.isErr()) {
      throw new Error(
        "attempting to union schema whose types are 'incompatible'"
      );
    }

    return this.bareSchemaFromTy(tyRes.value);
  };

  static discriminatedUnion = <D extends string, Ts extends readonly object[]>(
    discriminator: D,
    options: DiscriminatedUnionOptions<D, Ts>
  ): Def<Ts[number]> => {
    const optionsArray = [...options];
    if (optionsArray.length === 0) {
      throw new Error('discriminatedUnion must have at least one option');
    }

    // Validate each option is a struct with the discriminator field
    for (const opt of optionsArray) {
      if (opt.ty.ty.k !== 'struct') {
        throw new Error(
          `discriminatedUnion options must be struct schemas, got ${opt.ty.ty.k}`
        );
      }
      const fieldTy = opt.ty.ty.fields[discriminator];
      if (fieldTy === undefined) {
        throw new Error(
          `discriminatedUnion option missing discriminator key "${discriminator}"`
        );
      }
      if (fieldTy.ty.k !== 'enum') {
        throw new Error(
          `discriminatedUnion discriminator "${discriminator}" must be a literal/enum in each option`
        );
      }
    }

    const tyRes = tryUnion(optionsArray.map((opt) => opt.ty));
    if (tyRes.isErr()) {
      throw new Error(
        "attempting to discriminatedUnion schema whose types are 'incompatible'"
      );
    }

    const innerSchema = this.bareSchemaFromTy(tyRes.value) as Schema<
      Ts[number]
    >;

    return new DiscriminatedUnionSchema(
      innerSchema,
      discriminator,
      optionsArray as Def<Ts[number]>[]
    );
  };

  static lazy = <T>(fn: () => Def<T>): Def<T> => new LazySchema(fn);

  static recordWithKey = <V>(
    keySchema: zod.ZodType<string>,
    valueSchema: Def<V>
  ): Def<Record<string, V>> => new RecordWithKeySchema(keySchema, valueSchema);

  /**
   *
   */
  private static readonly _GlobalSchemaStore: WeakMap<
    Ty.ImmutableTy,
    Def<any>
  > = new WeakMap();

  private readonly _immutableTy: Ty.ImmutableTy;
  private readonly _shape: SchemaShape<T>;
  private readonly _description: string | null;
  private readonly _defaultValue: DefaultValue<T>;
  private readonly _constructorOpts: {
    description?: string | null;
    defaultValue?: DefaultValue<T>;
  };

  private constructor(
    immutableTy: Ty.ImmutableTy,
    opts?: {
      description?: string | null;
      defaultValue?: DefaultValue<T>;
    }
  ) {
    if (Schema._GlobalSchemaStore.has(immutableTy)) {
      throw new Error(
        'cannot instantiate a schema with an existing immutableTy'
      );
    }

    this._immutableTy = immutableTy;

    this._constructorOpts = opts ?? {};

    this._description = opts?.description ?? null;
    this._defaultValue = opts?.defaultValue ?? { specified: false };

    this._shape = (() => {
      switch (immutableTy.ty.k) {
        case 'primitive':
        case 'range':
        case 'enum':
          return { t: 'base' };

        case 'array': {
          // exists by factory class precondition
          const elementSchema = Assert.assertExists(
            Schema._GlobalSchemaStore.get(immutableTy.ty.t)
          );

          return { t: 'array', elementSchema };
        }

        case 'record': {
          // exists by factory class precondition
          const recordValueSchema = Assert.assertExists(
            Schema._GlobalSchemaStore.get(immutableTy.ty.t)
          );

          return { t: 'record', recordValueSchema };
        }

        case 'struct': {
          const fieldsSchema = _.mapValues(
            immutableTy.ty.fields,
            // exists by factory class precondition
            (fieldTy) =>
              Assert.assertExists(Schema._GlobalSchemaStore.get(fieldTy))
          ) as { [Key in keyof T]: Def<T[Key]> };

          return { t: 'struct', fieldsSchema };
        }

        default:
          return Assert.unreachable(immutableTy.ty);
      }
    })();

    Schema._GlobalSchemaStore.set(immutableTy, this);

    Object.freeze(this);
  }

  private static createZodSchema<T>(
    immutableTy: Ty.ImmutableTy,
    description: string | null,
    defaultValue: DefaultValue<T>,
    opts: { strict?: boolean }
  ): ZodSchema<T> {
    const withNullable: ZodSchema = (() => {
      switch (immutableTy.ty.k) {
        case 'primitive':
        case 'range':
        case 'enum':
          return validatorForType(immutableTy, opts);
        case 'array':
        case 'record':
        case 'struct': {
          const withoutNullable: ZodSchema = (() => {
            switch (immutableTy.ty.k) {
              case 'array': {
                const eltTy: Ty.ImmutableTy = immutableTy.ty.t;

                // exists by Factory class precondition
                const eltErod = Assert.assertExists(
                  Schema._GlobalSchemaStore.get(eltTy)
                );

                return eltErod.zodSchema(opts).array();
              }

              case 'record': {
                const recordValueTy: Ty.ImmutableTy = immutableTy.ty.t;

                // exists by Factory class precondition
                const recordValueErod = Assert.assertExists(
                  Schema._GlobalSchemaStore.get(recordValueTy)
                );

                return zod.record(recordValueErod.zodSchema(opts));
              }

              case 'struct': {
                const fieldsTy: { [name: string]: Ty.ImmutableTy } =
                  immutableTy.ty.fields;

                const fieldsErod = _.mapValues(fieldsTy, (fieldTy) =>
                  // exists by Factory class precondition
                  Assert.assertExists(Schema._GlobalSchemaStore.get(fieldTy))
                );

                const fieldsZod = _.mapValues(fieldsErod, (erod) =>
                  erod.zodSchema(opts)
                );

                return zod.object(fieldsZod);
              }
            }
          })();

          return immutableTy.nullable
            ? opts.strict
              ? withoutNullable.nullable()
              : withoutNullable.nullish().default(null)
            : withoutNullable;
        }

        default:
          return Assert.unreachable(immutableTy.ty);
      }
    })();

    const withDefault = defaultValue.specified
      ? withNullable.default(defaultValue.val)
      : withNullable;

    const withDescription = description
      ? withDefault.describe(description)
      : withDefault;

    return withDescription;
  }

  /**
   * Creates a recursively bare (without defaults/descriptions, ets) schema from ty.
   *
   * You most likely do not want to use this method; and instead should use other factory methods, as
   * this method may lose descriptions/defaults.
   */
  static bareSchemaFromTy(ty: ExtendedAttributeType): Def<unknown> {
    const withoutNullable = (() => {
      switch (ty.ty.k) {
        case 'primitive': {
          switch (ty.ty.t) {
            case 'boolean':
              return Schema.boolean();
            case 'day':
            case 'month':
            case 'year':
              return Schema.date();
            case 'timestamp':
              return Schema.timestamp();

            case 'int':
              return Schema.int();
            case 'float':
              return Schema.float();
            case 'string':
              return Schema.string();

            case 'super':
              return Schema.super();

            default:
              return Assert.unreachable(ty.ty);
          }
        }

        case 'range': {
          // A range is an integer bounded by [start, end]; treat it as an int
          // (consistent with how ranges validate elsewhere in this file).
          return Schema.int();
        }

        case 'enum':
          return Schema.enum(ty.ty.t);

        case 'array':
          return Schema.array(this.bareSchemaFromTy(ty.ty.t));

        case 'record':
          return Schema.record(this.bareSchemaFromTy(ty.ty.t));

        case 'struct':
          // Hacky band-aid: make nullable field structs default null
          return Schema.struct(
            _.mapValues(ty.ty.fields, (fieldTy) => {
              const fieldSchema = this.bareSchemaFromTy(fieldTy);
              return fieldSchema.isNullable
                ? fieldSchema.default(null)
                : fieldSchema;
            })
          );

        default:
          return Assert.unreachable(ty.ty);
      }
    })();

    return ty.nullable ? withoutNullable.nullable() : withoutNullable;
  }
}

export const z: SchemaFactory = Schema;

/**
 * Distributes the Def<U> type over a list of unknown[] types.
 *
 * Usage:
 *
 * ```TypeScript
 * type Original = [string, number, boolean];
 * type Distributed = DistributeDef<Original>;
 * // Result: [Def<string>, Def<number>, Def<boolean>]
 * ```
 */
type DistributeDef<T extends readonly unknown[]> = {
  [K in keyof T]: Def<T[K]>;
};

/**
 * Options for discriminated union - each must be a Def for a struct with the discriminator key.
 */
type DiscriminatedUnionOptions<
  _D extends string,
  Ts extends readonly object[]
> = {
  [K in keyof Ts]: Def<Ts[K]>;
};

const validatorForType = (
  shorthand: Ty.Shorthand,
  opts?: {
    rules?: [Ty.PrimitiveAttributeType, zod.ZodType][];
    strict?: boolean;
  }
): zod.ZodType<Ty.Scalar> => {
  const eTy = shorthandToTy(shorthand);
  const { ty, nullable } = eTy;
  const primitiveValidators = Object.fromEntries(
    opts?.rules ?? PRIMITIVE_TYPE_INFERENCE_RULES
  );

  let outputSchema: zod.ZodType | null = null;

  if (ty.k === 'primitive') {
    if (ty.t === 'super') {
      outputSchema = zod.any();
    } else if (ty.t === 'timestamp') {
      outputSchema = zod.date({ coerce: true });
    } else {
      outputSchema = primitiveValidators[ty.t] ?? null;
    }
  }

  if (ty.k === 'array') {
    outputSchema = zod.array(validatorForType(ty.t, opts));
  }

  if (ty.k === 'enum') {
    if (ty.t.length > 0) {
      outputSchema = zod.enum(ty.t as [string, ...string[]]);
    } else {
      outputSchema = nullable ? zod.null() : zod.never();
    }
  }

  if (ty.k === 'record') {
    outputSchema = zod.record(
      zod.string(),
      validatorForType(Ty.nn(ty.t), opts)
    );
  }

  if (ty.k === 'range') {
    outputSchema = zod.number().min(ty.start).max(ty.end);
  }

  if (ty.k === 'struct') {
    outputSchema = zod.object(
      _.mapValues(ty.fields, (ty) => validatorForType(ty, opts)) as Record<
        string,
        zod.ZodType<Ty.Scalar>
      >
    );
  }

  if (!outputSchema) {
    throw new Error(`Logic error on ${JSON.stringify(ty)}`);
  }

  if (!nullable) {
    return outputSchema;
  }
  outputSchema = opts?.strict
    ? outputSchema.nullable()
    : outputSchema.nullish().default(null);

  return outputSchema;
};
